import Header from "@/components/Header";
import Footer from "@/components/Footer";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Phone, 
  Mail, 
  MessageCircle, 
  MapPin, 
  Clock,
  Send,
  Loader2
} from "lucide-react";
import { useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";

const Contact = () => {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    memberNumber: "",
    regarding: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const validateEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validatePhone = (phone: string) => {
    return /^[+]?[\d\s\-\(\)]{10,}$/.test(phone);
  };

  const sanitizeInput = (input: string) => {
    return input.trim().replace(/<[^>]*>/g, '');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!formData.fullName || !formData.email || !formData.phone || !formData.regarding || !formData.message) {
      toast({
        variant: "destructive",
        title: "Validation Error",
        description: "Please fill in all required fields.",
      });
      return;
    }

    if (!validateEmail(formData.email)) {
      toast({
        variant: "destructive",
        title: "Invalid Email",
        description: "Please enter a valid email address.",
      });
      return;
    }

    if (!validatePhone(formData.phone)) {
      toast({
        variant: "destructive",
        title: "Invalid Phone Number",
        description: "Please enter a valid phone number.",
      });
      return;
    }

    if (formData.message.length < 10) {
      toast({
        variant: "destructive",
        title: "Message Too Short",
        description: "Please provide a more detailed message (at least 10 characters).",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Sanitize inputs
      const sanitizedData = {
        name: sanitizeInput(formData.fullName),
        email: sanitizeInput(formData.email),
        subject: `${formData.regarding}${formData.memberNumber ? ` (Member: ${sanitizeInput(formData.memberNumber)})` : ''}`,
        message: `Phone: ${sanitizeInput(formData.phone)}\n\n${sanitizeInput(formData.message)}`,
      };

      const { error } = await supabase
        .from('contact_messages')
        .insert([sanitizedData]);

      if (error) {
        throw error;
      }

      toast({
        title: "Message Sent Successfully!",
        description: "Thank you for contacting us. Our team will review your message and respond within 24 business hours.",
      });

      // Reset form
      setFormData({
        fullName: "",
        email: "",
        phone: "",
        memberNumber: "",
        regarding: "",
        message: ""
      });

    } catch (error: any) {
      console.error('Contact form error:', error);
      toast({
        variant: "destructive",
        title: "Error Sending Message",
        description: "There was a problem sending your message. Please try again or contact us directly.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const communicationChannels = [
    {
      icon: Phone,
      title: "Talk to Us",
      content: "For immediate assistance or general inquiries, please call our dedicated member support team.",
      details: [
        "Phone: +254111485951",
        "Monday - Friday: 8:00 AM - 5:00 PM",
        "Saturday: 9:00 AM - 1:00 PM"
      ]
    },
    {
      icon: Mail,
      title: "Email Us",
      content: "For detailed inquiries, support requests, or feedback, send us an email and we will respond promptly.",
      details: [
        "For inquiries, email natconsacco@nca.go.ke"
      ]
    },
    {
      icon: MessageCircle,
      title: "Chat on WhatsApp",
      content: "For quick questions and on-the-go support, start a chat with us directly on WhatsApp.",
      details: ["WhatsApp Number: +254111485951"],
      hasButton: true
    },
    {
      icon: MapPin,
      title: "Visit Our Head Office",
      content: "We welcome you to visit our main office for in-person service.",
      details: [
        "KCB Towers, Kenya Road",
        "Housed in National Construction Authority NCA",
        "P.O. Box 21046-00100"
      ]
    }
  ];


  return (
    <div className="min-h-screen">
      <Header />
      <main className="container mx-auto px-4 py-16 space-y-16">
        {/* Page Title and Intro */}
        <section className="text-center space-y-4">
          <h1 className="text-4xl font-bold text-foreground">Contact Us</h1>
          <h2 className="text-2xl text-muted-foreground">We're Here to Help. Let's Connect.</h2>
          <p className="text-lg text-muted-foreground max-w-4xl mx-auto">
            Whether you are a valued member with a question or a prospective member interested in joining our family, 
            we are ready to assist you. Please use your preferred channel below to get in touch with the Natcon Sacco team. 
            We look forward to hearing from you.
          </p>
        </section>

        {/* Key Communication Channels */}
        <section className="space-y-8">
          <h3 className="text-2xl font-bold text-center text-foreground">Get in Touch</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {communicationChannels.map((channel, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                <CardHeader className="space-y-4">
                  <div className="w-16 h-16 mx-auto bg-primary/10 rounded-full flex items-center justify-center">
                    <channel.icon className="w-8 h-8 text-primary" />
                  </div>
                  <CardTitle className="text-lg">{channel.title}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-muted-foreground">{channel.content}</p>
                  <div className="space-y-2">
                    {channel.details.map((detail, idx) => (
                      <p key={idx} className="text-sm font-medium">{detail}</p>
                    ))}
                  </div>
                  {channel.hasButton && (
                    <Button 
                      className="w-full bg-green-600 hover:bg-green-700 text-white"
                      onClick={() => window.open("https://wa.me/254111485951", "_blank")}
                    >
                      Start Chat
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Interactive Contact Form */}
        <section className="space-y-8">
          <div className="text-center">
            <h3 className="text-2xl font-bold text-foreground">Send Us a Message Directly</h3>
          </div>
          <Card className="max-w-4xl mx-auto">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="fullName">Full Name *</Label>
                    <Input
                      id="fullName"
                      placeholder="Enter your full name"
                      value={formData.fullName}
                      onChange={(e) => handleInputChange("fullName", e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="Enter your email address"
                      value={formData.email}
                      onChange={(e) => handleInputChange("email", e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input
                      id="phone"
                      placeholder="Enter your phone number"
                      value={formData.phone}
                      onChange={(e) => handleInputChange("phone", e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="memberNumber">Member Number (Optional)</Label>
                    <Input
                      id="memberNumber"
                      placeholder="Enter your member number (if applicable)"
                      value={formData.memberNumber}
                      onChange={(e) => handleInputChange("memberNumber", e.target.value)}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="regarding">Regarding *</Label>
                  <Select value={formData.regarding} onValueChange={(value) => handleInputChange("regarding", value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select the nature of your inquiry" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">General Inquiry</SelectItem>
                      <SelectItem value="membership">New Membership Questions</SelectItem>
                      <SelectItem value="loans">Loan Products & Services</SelectItem>
                      <SelectItem value="technical">Technical Support (Member Portal/App)</SelectItem>
                      <SelectItem value="feedback">Feedback & Complaints</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="message">Your Message *</Label>
                  <Textarea
                    id="message"
                    placeholder="Type your message here..."
                    value={formData.message}
                    onChange={(e) => handleInputChange("message", e.target.value)}
                    rows={5}
                    required
                  />
                </div>
                <Button type="submit" className="w-full md:w-auto bg-gradient-primary hover:opacity-90" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  {isSubmitting ? "Sending..." : "Send Message"}
                </Button>
              </form>
            </CardContent>
          </Card>
        </section>

        {/* Our Location */}
        <section className="space-y-8">
          <div className="text-center">
            <h3 className="text-2xl font-bold text-foreground">Visit Our Head Office</h3>
          </div>
          
          {/* Interactive Google Map */}
          <div className="rounded-lg overflow-hidden shadow-lg">
            <iframe 
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3988.78906522024!2d36.81012267395966!3d-1.3014632986861743!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x182f113e4dfeb6b5%3A0x6dade9903cec59b6!2sNational%20Construction%20Authority%20Headquarters!5e0!3m2!1sen!2ske!4v1754479238486!5m2!1sen!2ske" 
              width="100%" 
              height="400" 
              style={{ border: 0 }} 
              allowFullScreen 
              loading="lazy" 
              referrerPolicy="no-referrer-when-downgrade"
              title="Natcon Sacco at National Construction Authority Headquarters"
            />
          </div>
        </section>
      </main>
      <Footer />
      
    </div>
  );
};

export default Contact;